import {
	isPeopleOnboardingAnswerComplete,
	isPeopleOnboardingAnswerCommentComplete,
	PEOPLE_ONBOARDING_QUESTION_TYPES,
	normalizePeopleOnboardingAnswerComment,
	normalizePeopleOnboardingAnswerValue,
	sanitizePeopleOnboardingQuestions,
} from './onboardingQuestions';

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);
const MAX_FORM_DESCRIPTION_LENGTH = 4000;

const sanitizeCompanyDocumentFormDescription = (value) => (
	String(value || '').trim().slice(0, MAX_FORM_DESCRIPTION_LENGTH)
);

export const normalizeFormQuestionLabelDraft = (value) => {
	if (typeof value !== 'string') {
		return '';
	}

	return value.replace(/\s+/g, ' ');
};

const normalizeFormAnswersMap = (rawAnswers) => {
	if (!rawAnswers) {
		return {};
	}

	if (Array.isArray(rawAnswers)) {
		return rawAnswers.reduce((acc, item) => {
			const questionId = String(item?.questionId || '').trim();
			if (!questionId || acc[questionId]) {
				return acc;
			}

			acc[questionId] = {
				value: item?.value,
				comment: item?.comment,
			};
			return acc;
		}, {});
	}

	if (typeof rawAnswers === 'object') {
		return rawAnswers;
	}

	return {};
};

const resolveAnswerInputValue = (answerInput) => {
	if (answerInput && typeof answerInput === 'object' && (
		hasOwn(answerInput, 'value') ||
		hasOwn(answerInput, 'comment')
	)) {
		return answerInput.value;
	}

	return answerInput;
};

const resolveAnswerInputComment = (answerInput) => {
	if (answerInput && typeof answerInput === 'object' && hasOwn(answerInput, 'comment')) {
		return answerInput.comment;
	}

	return '';
};

export const sanitizeCompanyDocumentFormConfig = (rawConfig) => {
	const sourceQuestions = Array.isArray(rawConfig?.questions)
		? rawConfig.questions
		: (Array.isArray(rawConfig) ? rawConfig : []);
	const questions = sanitizePeopleOnboardingQuestions(sourceQuestions);
	const reviewQuestions = sanitizePeopleOnboardingQuestions(rawConfig?.reviewQuestions);
	const description = sanitizeCompanyDocumentFormDescription(rawConfig?.description);
	const hasAnonymousResponsesSetting = hasOwn(rawConfig, 'anonymousResponses');

	return {
		description,
		questions,
		reviewQuestions,
		...(hasAnonymousResponsesSetting ? {
			anonymousResponses: rawConfig?.anonymousResponses === true,
		} : {}),
	};
};

export const isAnonymousCompanyDocumentForm = formConfig => (
	formConfig?.anonymousResponses === true
);

const resolveCompanyDocumentQuestionAnswerStatus = ({
	description = '',
	questions = [],
	formAnswers,
	allowOptionalComments = false,
	commentMaxLength,
}) => {
	if (!questions.length) {
		return {
			description,
			questions,
			normalizedAnswersById: {},
			normalizedCommentsById: {},
			sanitizedFormAnswerMap: {},
			answerRecords: [],
			missingRequiredLabels: [],
			missingRequiredCommentLabels: [],
			commentLengthExceededLabels: [],
			isComplete: true,
		};
	}

	const answerInputMap = normalizeFormAnswersMap(formAnswers);
	const normalizedAnswersById = {};
	const normalizedCommentsById = {};
	const sanitizedFormAnswerMap = {};
	const answerRecords = [];
	const missingRequiredLabels = [];
	const missingRequiredCommentLabels = [];
	const commentLengthExceededLabels = [];
	const resolvedCommentMaxLength = Number.isInteger(commentMaxLength) && commentMaxLength > 0
		? commentMaxLength
		: null;

	for (const question of questions) {
		const questionId = String(question?.id || '').trim();
		if (!questionId) {
			continue;
		}

		if (question?.type === PEOPLE_ONBOARDING_QUESTION_TYPES.HEADING) {
			continue;
		}

		const answerInput = answerInputMap?.[questionId];
		const normalizedValue = normalizePeopleOnboardingAnswerValue(
			question,
			resolveAnswerInputValue(answerInput),
		);
		normalizedAnswersById[questionId] = normalizedValue;

		const rawComment = resolveAnswerInputComment(answerInput);
		if (
			resolvedCommentMaxLength &&
			String(rawComment ?? '').length > resolvedCommentMaxLength
		) {
			commentLengthExceededLabels.push(question.label);
		}

		const normalizedComment = normalizePeopleOnboardingAnswerComment(
			question,
			rawComment,
			normalizedValue,
			{
				allowOptionalComment: allowOptionalComments,
				...(resolvedCommentMaxLength ? { maxLength: resolvedCommentMaxLength } : {}),
			},
		);
		if (normalizedComment) {
			normalizedCommentsById[questionId] = normalizedComment;
		}

		sanitizedFormAnswerMap[questionId] = {
			value: normalizedValue,
			...(normalizedComment ? { comment: normalizedComment } : {}),
		};

		answerRecords.push({
			questionId,
			label: question.label,
			type: question.type,
			required: Boolean(question.required),
			allowMultiple: question.allowMultiple === true,
			value: normalizedValue,
			...(normalizedComment ? { comment: normalizedComment } : {}),
		});

		if (!isPeopleOnboardingAnswerComplete(question, normalizedValue)) {
			missingRequiredLabels.push(question.label);
		}
		else if (!isPeopleOnboardingAnswerCommentComplete(
			question,
			normalizedValue,
			rawComment,
		)) {
			missingRequiredLabels.push(question.label);
			missingRequiredCommentLabels.push(question.label);
		}
	}

	return {
		description,
		questions,
		normalizedAnswersById,
		normalizedCommentsById,
		sanitizedFormAnswerMap,
		answerRecords,
		missingRequiredLabels,
		missingRequiredCommentLabels,
		commentLengthExceededLabels,
		isComplete: missingRequiredLabels.length === 0,
	};
};

export const resolveCompanyDocumentFormAnswerStatus = ({
	formConfig,
	formAnswers,
	allowOptionalComments = false,
	commentMaxLength,
}) => {
	const {
		description,
		questions,
	} = sanitizeCompanyDocumentFormConfig(formConfig);

	return resolveCompanyDocumentQuestionAnswerStatus({
		description,
		questions,
		formAnswers,
		allowOptionalComments,
		commentMaxLength,
	});
};

export const resolveCompanyDocumentReviewAnswerStatus = ({
	formConfig,
	reviewAnswers,
}) => {
	const { reviewQuestions } = sanitizeCompanyDocumentFormConfig(formConfig);

	return resolveCompanyDocumentQuestionAnswerStatus({
		questions: reviewQuestions,
		formAnswers: reviewAnswers,
	});
};
